/**
 * Browser half of the Mufid-only Meet Space OAuth seam.
 *
 * The PKCE verifier and OAuth result are held in this component's memory only.
 * A reload, close, or popup failure loses them and requires a new authorization;
 * nothing is written to localStorage, sessionStorage, a cookie, or React Query.
 */
import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { sessionPortalApi } from "@/lib/api/sessionPortal";
import { ApiError } from "@/lib/api/client";
import {
  GOOGLE_MEET_OAUTH_MESSAGE_TYPE,
  googleMeetOAuthChannelName,
  type GoogleMeetOAuthMessage,
} from "@/lib/google-meet-oauth";

function encodeBase64Url(bytes: Uint8Array): string {
  let text = "";
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

async function pkcePair(): Promise<{ verifier: string; challenge: string }> {
  const bytes = new Uint8Array(48);
  crypto.getRandomValues(bytes);
  const verifier = encodeBase64Url(bytes);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: encodeBase64Url(new Uint8Array(digest)) };
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return typeof error.detail === "string" ? error.detail : "Google Meet creation could not be completed.";
  }
  return "Google Meet creation could not be completed.";
}

export function GoogleMeetSpaceControl({
  sessionId,
  replace,
  variant = "outline",
  size = "default",
  className,
  onCompleted,
}: {
  sessionId: string;
  replace: boolean;
  variant?: "default" | "outline";
  size?: "default" | "lg";
  className?: string;
  onCompleted: () => void;
}) {
  const verifiers = useRef(new Map<string, string>());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setError(null);
    setWorking(true);
    // Open synchronously from the gesture so browser popup protection does not
    // turn a valid authorization into a silent failure.
    const popup = window.open("", "lms-google-meet", "popup,width=520,height=700");
    if (!popup) {
      setWorking(false);
      setError("Allow the Google authorization popup, then try again.");
      return;
    }
    try {
      const { verifier, challenge } = await pkcePair();
      const authorization = await sessionPortalApi.googleMeetAuthorization(sessionId, {
        code_challenge: challenge,
        replace,
      });
      verifiers.current.set(authorization.state, verifier);
      // Older browsers can retain the opener bridge. Modern browsers may
      // isolate the Google navigation from it, so prefer the same-origin
      // in-memory channel whenever it is available.
      const callbackChannel = typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel(googleMeetOAuthChannelName(authorization.state));
      let settled = false;
      const finish = async (message: GoogleMeetOAuthMessage) => {
        if (
          settled
          || message.type !== GOOGLE_MEET_OAUTH_MESSAGE_TYPE
          || message.state !== authorization.state
        ) return;
        settled = true;
        window.removeEventListener("message", receive);
        callbackChannel?.removeEventListener("message", receiveBroadcast);
        callbackChannel?.close();
        const state = authorization.state;
        const code = typeof message.code === "string" ? message.code : "";
        const popupError = typeof message.error === "string" ? message.error : "";
        const codeVerifier = verifiers.current.get(state);
        verifiers.current.delete(state);
        if (popupError || !state || !code || !codeVerifier) {
          setError(popupError || "Google authorization did not return a usable result.");
          setWorking(false);
          return;
        }
        try {
          await sessionPortalApi.completeGoogleMeetAuthorization(sessionId, {
            state,
            code,
            code_verifier: codeVerifier,
            replace,
          });
          onCompleted();
        } catch (completionError) {
          setError(errorMessage(completionError));
        } finally {
          setWorking(false);
        }
      };
      const receive = (event: MessageEvent<GoogleMeetOAuthMessage>) => {
        if (event.origin !== window.location.origin || event.source !== popup) return;
        void finish(event.data);
      };
      const receiveBroadcast = (event: MessageEvent<GoogleMeetOAuthMessage>) => {
        void finish(event.data);
      };
      window.addEventListener("message", receive);
      callbackChannel?.addEventListener("message", receiveBroadcast);
      popup.location.assign(authorization.authorization_url);
    } catch (startError) {
      popup.close();
      setWorking(false);
      setError(errorMessage(startError));
    }
  }

  return (
    <div className="grid gap-2">
      <Button type="button" size={size} variant={variant} className={className} onClick={() => void begin()} disabled={working}>
        {working && <Loader2 aria-hidden className="mr-1.5 size-4 animate-spin" />}
        {replace ? "Replace Google Meet" : "Create Google Meet"}
      </Button>
      {error && <p role="status" className="text-ui text-destructive">{error}</p>}
    </div>
  );
}
